import React from 'react';
import { Card, BlockStack, Text, TextField, Checkbox, InlineStack, RadioButton } from '@shopify/polaris';

const SHOPIFY_FINANCIAL_STATUSES = [
  { label: 'Paid', value: 'paid' },
  { label: 'Pending', value: 'pending' },
  { label: 'Authorized', value: 'authorized' },
  { label: 'Partially Paid', value: 'partially_paid' },
  { label: 'Partially Refunded', value: 'partially_refunded' },
  { label: 'Refunded', value: 'refunded' },
  { label: 'Voided', value: 'voided' },
];

const MAGENTO_STATUSES = [
  { label: 'Processing', value: 'processing' },
  { label: 'Pending', value: 'pending' },
  { label: 'Complete', value: 'complete' },
  { label: 'Closed', value: 'closed' },
  { label: 'Canceled', value: 'canceled' },
  { label: 'On Hold', value: 'holded' },
];

const ORDER_CREATION_OPTIONS = [
  { label: 'Create SO and customer deposit', value: 'so_deposit' },
  { label: 'Create SO, invoice at shipment', value: 'so_invoice' },
  { label: 'Record as cash sale', value: 'cash_sale' },
];

export function OrdersGeneralSection({
  value,
  onChange,
  bare = false,
  source,
  showApprovalStatus = true,
  creationTypeLabel = 'Online orders — create in NetSuite as'
}) {
  const update = (field, newValue) => onChange({ ...value, [field]: newValue });

  const isShopify = source === 'shopify';
  const statusOptions = isShopify ? SHOPIFY_FINANCIAL_STATUSES : MAGENTO_STATUSES;
  const statusField = isShopify ? 'allowedFinancialStatuses' : 'allowedStatuses';
  const defaultStatuses = isShopify ? ['paid'] : ['processing'];

  const content = (
      <BlockStack gap="loose">
        <TextField
          label="Order Prefix"
          value={value.orderPrefix || ''}
          onChange={(v) => update('orderPrefix', v)}
          placeholder="e.g., WEB-"
        />

        {showApprovalStatus && (
          <div>
            <Text variant="bodyMd" fontWeight="medium" as="h3">Default NetSuite order approval status</Text>
            <InlineStack gap="loose">
              <div style={{ marginRight: '24px' }}>
                <RadioButton
                  label="Pending Approval"
                  checked={value.approvalStatus === '1'}
                  onChange={() => update('approvalStatus', '1')}
                />
              </div>
              <div>
                <RadioButton
                  label="Approved"
                  checked={value.approvalStatus === '2'}
                  onChange={() => update('approvalStatus', '2')}
                />
              </div>
            </InlineStack>
          </div>
        )}

        <div>
          <Text variant="bodyMd" fontWeight="medium" as="h3">{creationTypeLabel}</Text>
          <BlockStack gap="tight">
            {ORDER_CREATION_OPTIONS.map((opt) => (
              <RadioButton
                key={opt.value}
                label={opt.label}
                checked={value.orderCreationType === opt.value}
                onChange={() => update('orderCreationType', opt.value)}
              />
            ))}
          </BlockStack>
        </div>

        <div>
          <Text variant="bodyMd" fontWeight="bold" as="h3">
            {isShopify ? 'Sync orders with financial status' : 'Add orders with status'}
          </Text>
          <InlineStack gap="loose" align="start" wrap>
            {statusOptions.map((opt) => {
              const selected = (value[statusField] || defaultStatuses).includes(opt.value);
              const toggle = (checked) => {
                const current = new Set(value[statusField] || defaultStatuses);
                if (checked) current.add(opt.value); else current.delete(opt.value);
                update(statusField, Array.from(current));
              };
              return (
                <div key={opt.value} style={{ marginRight: 12 }}>
                  <Checkbox
                    label={opt.label}
                    checked={selected}
                    onChange={toggle}
                  />
                </div>
              );
            })}
          </InlineStack>
        </div>

        <TextField
          label="Add orders created after"
          value={value.createdAfter || ''}
          onChange={(v) => update('createdAfter', v)}
          type="datetime-local"
        />

        <TextField
          label="NetSuite item to track gift card sale"
          value={value.giftCardItem || ''}
          onChange={(v) => update('giftCardItem', v)}
          placeholder="Enter the NetSuite internal ID for the item used to track gift card sales"
        />
        <TextField
          label="NetSuite item to track gift card redemption"
          value={value.giftCardRedemptionItem || ''}
          onChange={(v) => update('giftCardRedemptionItem', v)}
          placeholder="Enter the NetSuite internal ID for the item used to track gift card redemptions"
        />
        <TextField
          label="NetSuite item to track store credit redemption"
          value={value.storeCreditItem || ''}
          onChange={(v) => update('storeCreditItem', v)}
          placeholder="Enter the NetSuite internal ID for the item used to track store credit redemptions"
        />

        <TextField
          label="NetSuite item to track print card and gift wrap price"
          value={value.giftWrapItem || ''}
          onChange={(v) => update('giftWrapItem', v)}
          placeholder="Enter the NetSuite internal ID for the item used to track gift wrap pricing"
        />
      </BlockStack>
  );

  return bare ? content : (
    <Card sectioned>
      {content}
    </Card>
  );
}
